/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Box, Stack, TableCell, Typography, useTheme, useMediaQuery } from '@mui/material';

import { roundedTo, seedWeightForPaddockIgnoreIsMixing } from '../utils/calculateTotal';
import { StyledTableRow, StyledTableRowMobile, TableCellContent } from './SeedTableCell';
import {
  Paddock,
  SEED_MIX_TYPE,
  SeedProductLine,
  SeedTypesenseProduct,
} from '../types/SeedOrderFormType';
import { selectProduct } from '../../products/storage/stocks.slice';
import { EditButton } from '../../products/components/ProductCard/EditButton';
import { useSeedEditPriceModal } from './useSeedEditPriceModal';
import { CartItem } from '../../cart/storage/cart.types';
import { selectCurrentFarmerAccount } from '../../azure-b2c/storage';
import { formatPrice } from '../../../util/helpers';

export interface SeedOrderTableProductRowProps {
  paddock: Paddock;
  seedProduct: SeedProductLine;
  mixType?: SEED_MIX_TYPE;
}
export const SeedOrderTableProductRow = ({
  paddock,
  seedProduct,
  mixType,
}: SeedOrderTableProductRowProps) => {
  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.down('sm'));
  const currentAccount = useSelector(selectCurrentFarmerAccount);
  const accountNumber = currentAccount?.accountNumber;
  const isTfo = currentAccount?.hasTfoAccess || false;

  const { setOpen, setItem, setAccountNumber } = useSeedEditPriceModal();

  const product = useSelector(selectProduct(seedProduct.productId));
  const productSku = product?.itemNumber ?? '';
  const productName = product?.name ?? '';
  const unitDiscountedPriceExcGst =
    (seedProduct?.discountedPriceExcGst ?? 0) / seedProduct.quantity;
  const price = seedProduct.editedPriceExcGst ?? unitDiscountedPriceExcGst ?? 0;
  const priceLabel = formatPrice(price);
  const quantity = seedProduct.isMixingFee
    ? seedProduct.quantity
    : seedWeightForPaddockIgnoreIsMixing(seedProduct, paddock);
  const total = formatPrice(quantity * price);

  const unit = useMemo(() => {
    if (product && (product as unknown as SeedTypesenseProduct).unit)
      return (product as unknown as SeedTypesenseProduct).unit;
    return 'kg';
  }, [product]);

  const onClick = () => {
    setItem({ ...seedProduct, name: product?.name } as unknown as CartItem);
    setOpen(true);
    setAccountNumber(accountNumber);
  };

  if (isSm) {
    return (
      <StyledTableRowMobile
        key={`seedProductRow-${seedProduct.productId}`}
        display='flex'
        flexDirection={'column'}
        p={2}
        gap={1}
      >
        <Typography variant='body2' fontWeight='bold'>
          {productName}
        </Typography>
        <Typography variant='body2'>{`SKU: ${productSku}`}</Typography>
        <Box display='flex' gap={1}>
          <Typography variant='body2' fontWeight='bold' color={theme.colors.primary500}>
            Unit price
          </Typography>
          <Typography variant='body2'>{priceLabel}</Typography>
          {isTfo ? <EditButton onClick={onClick} /> : null}
        </Box>
        {mixType === SEED_MIX_TYPE.CUSTOM ? (
          <Box display='flex' gap={1}>
            <Typography variant='body2' fontWeight='bold' color={theme.colors.primary500}>
              Sowing rate
            </Typography>
            <Typography variant='body2'>{`${roundedTo(seedProduct.sowingRate)} kg/Ha`}</Typography>
          </Box>
        ) : null}
        <Box display='flex' gap={1}>
          <Box flex={1} gap={1}>
            <Typography variant='body2' fontWeight='bold' color={theme.colors.primary500}>
              Quantity
            </Typography>
            <Typography variant='body2'>{`${quantity} ${unit}`}</Typography>
          </Box>
          <Box flex={1} gap={1}>
            <Typography variant='body2' fontWeight='bold' color={theme.colors.primary500}>
              Total (GST excl.)
            </Typography>
            <Typography variant='body2'>{total}</Typography>
          </Box>
        </Box>
      </StyledTableRowMobile>
    );
  }

  return (
    <StyledTableRow key={`seedProductRow-${seedProduct.productId}`}>
      <TableCell>
        <TableCellContent label={productSku} textAlign='start' />
      </TableCell>
      <TableCell>
        <TableCellContent label={productName} textAlign='start' />
      </TableCell>
      {mixType === SEED_MIX_TYPE.CUSTOM ? (
        <TableCell>
          {seedProduct.isMixingFee ? null : (
            <TableCellContent label={`${roundedTo(seedProduct.sowingRate)} kg/Ha`} />
          )}
        </TableCell>
      ) : null}
      <TableCell>
        <TableCellContent label={`${quantity} ${unit}`} />
      </TableCell>
      <TableCell>
        <Stack alignItems='flex-end'>
          <TableCellContent label={priceLabel} />
          {isTfo ? <EditButton onClick={onClick} /> : null}
        </Stack>
      </TableCell>
      <TableCell>
        <TableCellContent label={total} />
      </TableCell>
    </StyledTableRow>
  );
};
